import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import type { ComponentLike } from '@glint/template';
import FormField from './form-field';

// ---------------------------------------------------------------------------
// Input – reimagined text input
// ---------------------------------------------------------------------------

/**
 * Supported HTML input types for the Input component.
 */
export type InputType =
  'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'date' | 'url';

/**
 * Size variants consistent with all reimagined form components.
 */
export type InputSize = 'sm' | 'md' | 'lg';

interface InputSignature {
  Args: {
    /**
     * Stable identity for automation: emitted as `data-test-hook` on this
     * component's root, alongside its `data-test-*` kind markers.
     *
     * The call site says WHICH instance this is; the component already says
     * what KIND it is. Together they give a key that survives a re-render,
     * which `id` does not — 74% of ids in these apps are Ember-generated
     * (`…-ember42`) and change whenever the component tree is rebuilt. One
     * hook covers the whole subtree: a descendant control is identified as
     * its nearest ancestor hook plus its own role.
     *
     * Consumed by the ux-audit's per-element interaction ledger to tell "a
     * change broke this control" from "the page re-rendered in a different
     * order". See tuitionio/tio-front-end#10057.
     *
     * @example <Input @testHook="people__search-employee-id" @value={{this.q}} />
     */
    testHook?: string;
    // --- Value ---
    /** Current input value */
    value?: unknown;
    /** Called on every keystroke with the new value */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- permissive callback contract: callers pass handlers typed with varied event types (InputEvent, Event, …)
    onChange?: (value: string, event?: any) => void;
    /** Legacy alias for onChange */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- permissive callback contract: callers pass handlers typed with varied event types
    onInput?: (value: string, event?: any) => void;
    /** Called when the input loses focus */
    onBlur?: () => void;

    // --- Presentation ---
    /** Label displayed above the input */
    label?: string;
    /** Hint text displayed below the input */
    hint?: string;
    /** Legacy alias for hint */
    description?: string;
    /** Placeholder text */
    placeholder?: string;
    /** HTML input type @default "text" */
    type?: InputType;
    /** Size variant @default "md" */
    size?: InputSize;

    // --- State ---
    /** Validation error messages */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable the input */
    isDisabled?: boolean;
    /** Make the input readonly */
    isReadonly?: boolean;

    // --- Extras ---
    /** Icon component rendered inside the input on the left */
    leadingIcon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Icon component rendered inside the input on the right */
    trailingIcon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** HTML inputmode attribute */
    inputmode?: string;
    /** HTML autocomplete attribute */
    autocomplete?: string;
    /** Max character length */
    maxlength?: number;
    /** HTML name attribute */
    name?: string;
    /** Legacy class API support */
    classes?: { base?: string };
    /** Legacy no-op API support */
    isClearable?: boolean;
  };
  Blocks: {
    startContent?: [];
    endContent?: [];
  };
  Element: HTMLInputElement;
}

// ---------------------------------------------------------------------------
// Icon sizes per input size — visual styling lives in the .app-input class.
// ---------------------------------------------------------------------------

const ICON_SIZES: Record<InputSize, number> = { sm: 14, md: 16, lg: 18 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Input — a zero-dependency text input component.
 *
 * Controlled input that fires `@onChange` on every keystroke.
 * Works standalone with `@value`/`@onChange`/`@errors`, or as a field inside the
 * `<Form>` orchestrator (`tio-ui/components/form`).
 *
 * Styling matches the reimagined Select: violet focus rings, sm/md/lg sizes,
 * consistent dark mode treatment, and `...attributes` for escape-hatch
 * customisation (e.g. modifiers like `{{inputmask}}`).
 *
 * @example Basic usage
 * ```gts
 * import Input from 'tio-ui/components/input';
 *
 * <Input
 *   @label="Email"
 *   @type="email"
 *   @value={{this.email}}
 *   @onChange={{this.setEmail}}
 *   @placeholder="you@example.com"
 *   @isRequired={{true}}
 * />
 * ```
 *
 * @example With errors
 * ```gts
 * <Input
 *   @label="Name"
 *   @value={{this.name}}
 *   @onChange={{this.setName}}
 *   @errors={{array "Name is required"}}
 * />
 * ```
 *
 * @example With leading icon
 * ```gts
 * import { PhMagnifyingGlass } from 'ember-phosphor-icons';
 *
 * <Input
 *   @label="Search"
 *   @value={{this.query}}
 *   @onChange={{this.setQuery}}
 *   @leadingIcon={{PhMagnifyingGlass}}
 *   @placeholder="Search employees…"
 * />
 * ```
 *
 * @example With prefix/suffix content
 * ```gts
 * <Input
 *   @label="Amount"
 *   @value={{this.amount}}
 *   @onChange={{this.setAmount}}
 * >
 *   <:startContent>$</:startContent>
 *   <:endContent>USD</:endContent>
 * </Input>
 * ```
 */
export default class Input extends Component<InputSignature> {
  private _fieldId = `reimagined-input-${guidFor(this)}`;

  // ---- Derived state --------------------------------------------------------

  private get size(): InputSize {
    return this.args.size ?? 'md';
  }

  private get iconSize(): number {
    return ICON_SIZES[this.size];
  }

  private get type(): InputType {
    return this.args.type ?? 'text';
  }

  private get hint(): string | undefined {
    return this.args.hint ?? this.args.description;
  }

  private get inputValue(): string | number | undefined {
    if (typeof this.args.value === 'string' || typeof this.args.value === 'number') {
      return this.args.value;
    }

    if (this.args.value == null) {
      return undefined;
    }

    return String(this.args.value);
  }

  private get classesBase(): string {
    return this.args.classes?.base ?? '';
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  private get hasLeadingIcon(): boolean {
    return Boolean(this.args.leadingIcon);
  }

  private get hasTrailingIcon(): boolean {
    return Boolean(this.args.trailingIcon);
  }

  private get inputClasses(): string {
    const classes = ['app-input', `app-input--${this.size}`];

    // Icon padding still varies with size × icon presence, so it stays
    // inline as a structural concern. Everything theme/state is owned
    // by .app-input above.
    if (this.hasLeadingIcon) {
      classes.push(this.size === 'sm' ? 'pl-8' : this.size === 'lg' ? 'pl-11' : 'pl-10');
    }
    if (this.hasTrailingIcon) {
      classes.push(this.size === 'sm' ? 'pr-8' : this.size === 'lg' ? 'pr-11' : 'pr-10');
    }

    if (this.hasErrors) {
      classes.push('app-input--has-error');
    }

    return classes.join(' ');
  }

  // ---- Event handlers -------------------------------------------------------

  handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.args.onChange?.(input.value, event);
    this.args.onInput?.(input.value, event);
  };

  handleBlur = () => {
    this.args.onBlur?.();
  };

  // ---- Template -------------------------------------------------------------

  <template>
    <FormField
      @label={{@label}}
      @hint={{this.hint}}
      @errors={{@errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this._fieldId}}
      as |field|
    >
      <div class="relative">
        {{! Leading adornment }}
        {{#if (has-block "startContent")}}
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 tio-fg-disabled pointer-events-none"
            aria-hidden="true"
          >
            {{yield to="startContent"}}
          </span>
        {{else if this.hasLeadingIcon}}
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 tio-fg-disabled pointer-events-none"
            aria-hidden="true"
          >
            {{#let @leadingIcon as |Icon|}}
              {{#if Icon}}
                <Icon @size={{this.iconSize}} />
              {{/if}}
            {{/let}}
          </span>
        {{/if}}

        <input
          data-test-input
          data-test-hook={{@testHook}}
          id={{field.fieldId}}
          type={{this.type}}
          value={{this.inputValue}}
          name={{@name}}
          placeholder={{@placeholder}}
          disabled={{@isDisabled}}
          readonly={{@isReadonly}}
          required={{@isRequired}}
          inputmode={{@inputmode}}
          autocomplete={{@autocomplete}}
          maxlength={{@maxlength}}
          aria-invalid={{if field.hasErrors "true"}}
          aria-describedby={{if this.hint (concat field.fieldId "-hint")}}
          class={{concat
            this.inputClasses
            (if this.classesBase (concat " " this.classesBase) "")
            (if (has-block "startContent") " pl-10" "")
            (if (has-block "endContent") " pr-10" "")
          }}
          {{on "input" this.handleInput}}
          {{on "blur" this.handleBlur}}
          ...attributes
        />

        {{! Trailing adornment }}
        {{#if (has-block "endContent")}}
          <span
            class="absolute right-2.5 top-1/2 -translate-y-1/2 tio-fg-disabled pointer-events-none"
            aria-hidden="true"
          >
            {{yield to="endContent"}}
          </span>
        {{else if this.hasTrailingIcon}}
          <span
            class="absolute right-2.5 top-1/2 -translate-y-1/2 tio-fg-disabled pointer-events-none"
            aria-hidden="true"
          >
            {{#let @trailingIcon as |Icon|}}
              {{#if Icon}}
                <Icon @size={{this.iconSize}} />
              {{/if}}
            {{/let}}
          </span>
        {{/if}}
      </div>
    </FormField>
  </template>
}

export type { InputSignature };
